import { createTRPCProxyClient, httpLink } from '@trpc/client';
import type { AppRouter } from 'ledger-api/trpc';
import { getValidToken } from './auth/oauth_pkce.js';
import { resolveApiBaseUrl, type RuntimeTarget } from './api_target.js';
import { isAndroid, isNative } from './platform.js';
import { resolveAdminTransport } from './auth/admin_transport.js';
import { getNativeSessionToken } from './auth/native_session_store.js';

/**
 * Snapshots the current runtime — build env, Capacitor platform, browsing
 * hostname — into the plain input {@link resolveApiBaseUrl} and
 * `resolveOAuthResource` (`lib/api_target.ts`) take. Exported so
 * `lib/auth/oauth_pkce.ts` resolves its `resource` indicator from the SAME
 * snapshot this client resolves its API origin from: one source of truth for
 * "which environment is this bundle pointed at", never two that can drift.
 *
 * Reads `import.meta.env.DEV` as a direct member expression (not via a local
 * or a parameter) so Vite still statically replaces it and a production
 * bundle dead-code-eliminates every dev branch downstream of it — the same
 * constraint `lib/auth/dev_auth_bypass.ts`'s call sites are held to.
 */
export function currentRuntimeTarget(): RuntimeTarget {
  return {
    explicitApiUrl: import.meta.env.VITE_API_URL,
    explicitAppOrigin: import.meta.env.VITE_WAGEPRO_APP_ORIGIN,
    dev: import.meta.env.DEV,
    native: isNative(),
    android: isAndroid(),
    browserHostname:
      typeof window !== 'undefined' ? window.location.hostname : 'localhost',
  };
}

/**
 * Resolves the ledger-api base URL. Exported (rather than kept module-private)
 * so `lib/auth/oauth_pkce.ts` targets the same origin for its `/oauth/*`
 * calls as this client targets for `/trpc` — one source of truth for "where
 * is the API", never a second hardcoded `localhost:8787`.
 *
 * The resolution itself lives in `lib/api_target.ts` as a pure function so it
 * is unit-testable without `import.meta.env` or a Capacitor bridge; this
 * wrapper only supplies the runtime snapshot. See that module's docblock for
 * why the Capacitor shell cannot use the browser's `localhost:8787` dev
 * target.
 */
export function resolveApiUrl(): string {
  return resolveApiBaseUrl(currentRuntimeTarget());
}

/**
 * Vanilla tRPC proxy client for the ADMIN console (WagePro signup epic
 * pivot off OAuth).
 *
 * TRANSPORT IS RUNTIME-DEPENDENT — see `auth/admin_transport.ts`, which owns
 * the decision as a pure function:
 *
 *  - **In a browser** — `credentials: 'include'`, plus
 *    `Authorization: Bearer <oauth token>` ONLY while a bearer-backed
 *    Ledger-grant session is active — a flag {@link setAdminBearerFallback}
 *    toggles on when `resolveAuthBoot` resolves via its case (c) (Ledger-SSO
 *    handoff, no cookie identity — see `auth/auth_context.tsx`'s
 *    `tryLedgerGrantFirstBoot`) and off on sign-out / an unauthenticated
 *    boot. The bearer is a Ledger-SSO fallback for the production
 *    `app.wagepro.ai` → `api.ledgerpro.ai` cross-site case (#25227), where
 *    the Ledger `SameSite=Lax` session cookie is never sent. It is
 *    DELIBERATELY NOT attached on every request that happens to find a
 *    token in local storage: `wageproAuth.login` / `.signup` are called
 *    from this same client (see `screens/auth/wagepro_login_screen.tsx`
 *    and `wagepro_signup_screen.tsx`), and a stale-but-valid bearer would
 *    otherwise resolve as an OAuth identity and FORBIDDEN the login
 *    (`oauthFailClosedMiddleware`, "not available to API/agent tokens"),
 *    leaving the user unable to sign in with credentials until the
 *    refresh token expired. `apps/api/src/trpc/context.ts` resolves a
 *    cookie session BEFORE a bearer, so any topology where the cookie IS
 *    reachable behaves exactly as before this change.
 *  - **In the native Capacitor shell** the session travels as
 *    `Authorization: Bearer <session id>` with `credentials: 'omit'`. The
 *    shell's WebView origin (`https://app.wagepro.ai`) and this API
 *    (`https://api.ledgerpro.ai`) are different registrable domains, so the
 *    production `SameSite=Lax` cookie is dropped on every cross-site request
 *    and cookie transport cannot work there at all — signup succeeded and
 *    the next `wageproAuth.me` 401'd, bouncing the shipped Android app to
 *    `/login` forever. The session id is handed to the shell in the
 *    signup/login response body (`client: 'native-shell'`) and stored by
 *    `auth/native_session_store.ts`. `apps/api/src/trpc/context.ts` resolves
 *    a raw session id in a Bearer header through the same `resolveSession`
 *    path a cookie takes, so this is the SAME first-party session, not a
 *    second auth model. See
 *    `docs/adr/20260908_01_wagepro-native-shell-session-bearer.md`.
 *
 * `headers()` is async (precedent: {@link createBearerTrpcClient} below) so
 * the stored token can be read across the Capacitor bridge per request; the
 * store memoises, so this is a synchronous-in-practice lookup after boot.
 *
 * `headers()` and `fetch()` deliberately share NO mutable state. tRPC awaits
 * `headers()` and only then calls `fetch()`, but there are several microtask
 * hops in between (`@trpc/client`'s `fetchHTTPResponse` awaits the header
 * factory inside its own async IIFE), so two requests issued in the same tick
 * can interleave: request B's `headers()` can complete between request A's
 * `headers()` and A's `fetch()`. A variable written by one and read by the
 * other would therefore be cross-request state. `credentials` is instead
 * re-derived inside `fetch` from `isNative()` alone — synchronous, pure, and
 * process-constant — so there is nothing to interleave. Do not "optimise"
 * this back into a shared closure variable, and do not make `credentials`
 * depend on the token: that is precisely the edit that would turn a currently
 * harmless race into requests authenticating as each other.
 *
 * The WagePro admin signup/login path (`wageproAuth.{signup,login,me}`)
 * issues the standard `mukil_session`/`__Host-mukil_session` cookie directly
 * (no OAuth redirect, no consent screen — see
 * `apps/api/src/trpc/routers/wagepro_auth.ts`'s
 * module docblock), and every `payroll.*`/`entity.*`/`entityUser.*`
 * procedure this app calls already treats a session cookie as a trusted
 * first-party caller with ZERO code changes needed on the backend (the
 * pivotal fact the migration plan verified: `requireOAuthScope`
 * (`apps/api/src/trpc/init.ts`) and `assertMoneyWriteAllowed`'s OAuth checks
 * are explicit no-ops for session-cookie callers — `apps/ledger-web`'s own
 * payroll UI already proves this empirically, using nothing but
 * `credentials: 'include'`).
 *
 * This is now the DEFAULT client — every admin-console data hook
 * (`data/use_employees.ts`, `use_pay_runs.ts`, `use_settings.ts`, …) keeps
 * calling `getTrpcClient()` unchanged; only the TRANSPORT underneath it
 * flipped from Bearer to cookie. The one genuine exception is
 * `data/use_my_pay.ts` — the employee-facing "My Pay" ESS surface, which
 * per `docs/architecture-boundaries.md`'s OAuth-scoped-grant invariant and
 * `docs/security-personal-apps.md` MUST stay Bearer-only (a business
 * session cookie must never reach the Personal-Workspace plane, and vice
 * versa) — it now imports {@link getBearerTrpcClient} instead. See that
 * function's docblock for the full boundary rationale (moved here verbatim
 * from this function's own former docblock).
 */
/**
 * Module-level toggle for the web `Authorization: Bearer <oauth token>`
 * fallback. Off by default — `wageproAuth.login` / `.signup` are called from
 * this same admin client and MUST run as pure cookie-based requests, so
 * blindly attaching whatever OAuth token happens to sit in local storage
 * would resolve them as OAuth callers and FORBIDDEN
 * (`oauthFailClosedMiddleware`, "not available to API/agent tokens") until
 * the refresh token expires. {@link setAdminBearerFallback} flips it on ONLY
 * while a bearer-backed Ledger-grant session is active (`resolveAuthBoot`'s
 * case (c) — `auth/auth_context.tsx`'s `tryLedgerGrantFirstBoot`), and off
 * again on sign-out / an unauthenticated boot.
 */
let adminBearerFallbackEnabled = false;

/**
 * Enable or disable the web bearer fallback for this admin tRPC client.
 * Called by `AuthProvider` with `true` when a boot resolves via case (c)
 * (the Ledger-SSO cross-site handoff, #25227) and with `false` on sign-out
 * / an unauthenticated boot. See the {@link adminBearerFallbackEnabled}
 * comment for why the default is off.
 */
export function setAdminBearerFallback(enabled: boolean): void {
  adminBearerFallbackEnabled = enabled;
}

/**
 * Read the current flag — exported so tests can assert the toggle without
 * spying on every dep boundary.
 */
export function isAdminBearerFallbackEnabled(): boolean {
  return adminBearerFallbackEnabled;
}

/**
 * Injectable collaborators for {@link createTrpcClient}. Production passes
 * nothing; tests pass fakes so the ASSEMBLED transport (not just the pure
 * resolver) can be asserted without a Capacitor bridge or a network — a
 * refactor that keeps the identifiers but breaks the wiring must fail a test,
 * not just a `toContain` string check.
 */
export interface AdminTrpcClientDeps {
  /** Runtime detection. Defaults to `lib/platform.ts`'s `isNative`. */
  isNativeRuntime?: () => boolean;
  /** Stored native session id. Defaults to the real `native_session_store`. */
  getToken?: (opts: { native: boolean }) => Promise<string | null>;
  /**
   * Stored WagePro-audience OAuth access token (web-only). Defaults to
   * `lib/auth/oauth_pkce.ts`'s `getValidToken`. Read every request so a
   * mid-session token refresh is picked up on the next call. Consulted
   * ONLY when {@link isBearerFallbackEnabled} returns true — see the
   * module docblock's Ledger-SSO cross-site case (#25227) for the toggle
   * rationale.
   */
  getOAuthToken?: () => Promise<string | null>;
  /**
   * Reads the bearer-fallback flag. Defaults to the module-level state
   * that {@link setAdminBearerFallback} writes. Injectable so
   * `trpc.test.ts` can drive the header derivation without mutating shared
   * module state across tests.
   */
  isBearerFallbackEnabled?: () => boolean;
  /** Transport. Defaults to the global `fetch`. */
  fetchImpl?: (url: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

export function createTrpcClient(deps: AdminTrpcClientDeps = {}) {
  const {
    isNativeRuntime = isNative,
    getToken = getNativeSessionToken,
    getOAuthToken = getValidToken,
    isBearerFallbackEnabled = isAdminBearerFallbackEnabled,
    fetchImpl = globalThis.fetch.bind(globalThis),
  } = deps;
  return createTRPCProxyClient<AppRouter>({
    links: [
      httpLink({
        url: `${resolveApiUrl()}/trpc`,
        async headers() {
          const native = isNativeRuntime();
          const token = native ? await getToken({ native }) : null;
          const oauthToken =
            !native && isBearerFallbackEnabled() ? await getOAuthToken() : null;
          return resolveAdminTransport({ native, token, oauthToken }).headers;
        },
        fetch(url, options) {
          // Re-derived here rather than read from a variable `headers()`
          // wrote — see the docblock above on why sharing state between the
          // two is a cross-request hazard. `credentials` is a pure function
          // of `native` alone, so `token`/`oauthToken: null` is not a
          // simplification: the resolver returns the same credentials mode
          // either way. Do NOT couple `credentials` to the OAuth token or
          // the bearer-fallback flag either — that is exactly the
          // interleaving hazard the docblock above warns against.
          const { credentials } = resolveAdminTransport({
            native: isNativeRuntime(),
            token: null,
            oauthToken: null,
          });
          return fetchImpl(url, { ...options, credentials });
        },
      }),
    ],
  });
}

let _client: ReturnType<typeof createTrpcClient> | null = null;

export function getTrpcClient() {
  if (!_client) _client = createTrpcClient();
  return _client;
}

export function resetTrpcClient() {
  _client = null;
}

/**
 * Vanilla tRPC proxy client for the employee-facing "My Pay" ESS surface
 * ONLY (`data/use_my_pay.ts`). Sends the "Sign in with Ledger" OAuth access
 * token (if one is stored) as a Bearer header on every request — see
 * `lib/auth/oauth_pkce.ts`. Deliberately bearer-only: no `credentials:
 * 'include'`. `include` is the mode that DOES send cookies cross-origin (not
 * the restrictive one), and it would let a developer's `localhost` WagePro
 * ADMIN session cookie — SameSite=Lax, port-agnostic — ride along on these
 * cross-origin requests. `apps/api`'s context resolution prefers a session
 * cookie over a Bearer token when both are present, which would silently
 * authenticate the ESS surface as a full first-party BUSINESS session
 * instead of the identity-scoped, audience-bound OAuth token the
 * Personal-Workspace architecture (ADR-030,
 * `docs/adr/20260702_02_personal-workspace-tenancy.md`) requires — exactly
 * the cross-plane boundary `docs/architecture-boundaries.md`'s "OAuth-scoped
 * grants, not data copies" section and `docs/security-personal-apps.md`'s
 * "the personal app touches business data only as a scoped, audience-bound
 * OAuth Bearer — never a business session cookie" invariant exist to
 * preserve. This is the SAME function {@link getTrpcClient} used to be
 * (before the WagePro admin console pivoted off OAuth) — only the name and
 * its remaining caller changed.
 */
export function createBearerTrpcClient() {
  return createTRPCProxyClient<AppRouter>({
    links: [
      httpLink({
        url: `${resolveApiUrl()}/trpc`,
        async headers() {
          const token = await getValidToken();
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
      }),
    ],
  });
}

let _bearerClient: ReturnType<typeof createBearerTrpcClient> | null = null;

export function getBearerTrpcClient() {
  if (!_bearerClient) _bearerClient = createBearerTrpcClient();
  return _bearerClient;
}

export function resetBearerTrpcClient() {
  _bearerClient = null;
}

/**
 * Whether `err` is ANY tRPC FORBIDDEN rejection. Deliberately broad — a
 * single `apps/api` procedure can throw FORBIDDEN for several unrelated
 * reasons (missing OAuth scope, null audience, no entity access, an
 * inactive subscription, a missing add-on entitlement — see
 * `isPayrollAddonRequiredError` below for the one that matters to
 * `data/use_pay_runs.ts` and `lib/auth/oauth_pay_run_post_actions.ts`), so
 * this check alone does NOT tell you which one happened. Generic shape check
 * (not `err instanceof TRPCClientError`), so it works against both a real
 * `TRPCClientError` and any test double shaped `{ data: { code } }` — same
 * style as `lib/auth/step_up.ts`'s detectors.
 *
 * `lib/auth/trpc_errors.ts` used to carry an identically-shaped,
 * identically-named duplicate of this helper, shared by the still-dev
 * -bypass-only `dev_*.ts` resolvers this file's own guard note used to warn
 * against importing from. As of C12 (#22334) every one of those resolvers
 * is deleted (C1-C11 replaced each with a real, self-fetching hook), so that
 * duplicate module is deleted too — `data/use_employees.ts` (its last real
 * importer) now imports THIS copy directly, same as every other real,
 * OAuth-backed hook built on `getTrpcClient()` above (`use_pay_runs.ts`,
 * `oauth_pay_run_post_actions.ts`, C8 #22330, and future ones).
 */
export function isForbiddenTrpcError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('data' in err)) {
    return false;
  }
  const data = (err as { data?: unknown }).data;
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { code?: unknown }).code === 'FORBIDDEN'
  );
}

/**
 * Duck-typed check for a tRPC NOT_FOUND error — same shape/rationale as
 * {@link isForbiddenTrpcError}, for the "unsupported jurisdiction" gate
 * several LK-only procedures use (`assertLkJurisdiction`,
 * `apps/api/src/trpc/routers/filing_calendar.ts`) — a non-LK entity gets a
 * `NOT_FOUND` rather than a `FORBIDDEN`, specifically so the UI can classify
 * it as "not available for this entity's country" instead of "forbidden",
 * per `oauth_statutory_filing.ts`'s filing-calendar resolvers.
 */
export function isNotFoundTrpcError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('data' in err)) {
    return false;
  }
  const data = (err as { data?: unknown }).data;
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { code?: unknown }).code === 'NOT_FOUND'
  );
}

/**
 * The EXACT message `assertAddonEnabled` (`apps/api/src/lib/plan_gate.ts`)
 * throws via `requirePayrollAddon` (`apps/api/src/trpc/routers/payroll.ts`)
 * when an entity has no active `payroll` entitlement grant:
 * `` `This feature requires the ${humanAddonName(addonKey)} add-on.` `` with
 * `addonKey` always `'payroll'` on this middleware, so `humanAddonName`
 * (capitalize + de-underscore) always resolves to exactly `'Payroll'`.
 *
 * Unlike the OTHER FORBIDDEN causes a payroll procedure can throw
 * (`assertSubscriptionActive`'s `cause: { subscriptionRequired: true }`,
 * `assertPayrollProductTier`'s `cause: { requiredTier, currentTier }`),
 * `assertAddonEnabled` attaches NO machine-readable `cause` at all — so this
 * literal message string is the only discriminator the wire response
 * carries for this specific case. Message-text matching against a known,
 * stable backend literal is a precedented pattern in this codebase for
 * exactly this situation (see `apps/ledger-web/src/lib/trpc_error.ts`'s
 * `err.message === 'Forbidden'` check and
 * `lib/auth/dev_settings.ts`'s `CURRENCY_LOCKED_MESSAGE` match) — not a
 * novel hack introduced here.
 *
 * Cross-package literal pin (C8 second-round adversarial review, #22410):
 * `apps/api/src/lib/plan_gate.test.ts`'s `assertAddonEnabled > pins the
 * EXACT payroll add-on message the frontend exact-matches against (#22410)`
 * test pins this exact string on the backend side too, so a future rewording
 * of the message (or a `humanAddonName` change) can't silently degrade
 * `isPayrollAddonRequiredError` below with green CI on both sides.
 */
export const PAYROLL_ADDON_REQUIRED_MESSAGE =
  'This feature requires the Payroll add-on.';

/**
 * Whether `err` is SPECIFICALLY the FORBIDDEN `requirePayrollAddon` throws
 * for a missing `payroll` add-on entitlement (#21675) — as opposed to any
 * OTHER FORBIDDEN a `payroll.*` procedure can throw before ever reaching
 * that gate: a missing OAuth scope (`requireOAuthScope`), a null-audience
 * token (`requireNonNullAudience`), no entity access
 * (`fetchEntityRow`/`plan_gate.ts`), or an inactive subscription
 * (`assertSubscriptionActive`). All five throw `code: 'FORBIDDEN'`, so
 * `isForbiddenTrpcError` alone cannot tell them apart — conflating them was
 * exactly the bug this function fixes (adversarial review, PR #22370,
 * finding 2): treating EVERY FORBIDDEN as "buy the payroll add-on" would
 * mask a real auth bug (e.g. a missing OAuth-scope grant, C6 #21929) behind
 * a misleading billing prompt instead of surfacing the actual failure.
 *
 * `data/use_pay_runs.ts` and `lib/auth/oauth_pay_run_post_actions.ts` (C8
 * #22330) are the callers today — the other 4 causes resolve their generic
 * `error` state instead of `entitlement_required`.
 *
 * Also not reachable from `entity.clientGet`/`entity.update` today (C11,
 * #22333, the organisation-settings read/save path) — neither procedure is
 * gated behind `requirePayrollAddon` (`entity.clientGet` is a plain
 * entity-membership check; `entity.update` is a firm-membership check plus
 * an OAuth-only field allow-list — see `apps/api/src/trpc/routers/
 * entities.ts`). Checked there anyway for defensive correctness against the
 * same shared classifier every other C-child needs.
 */
export function isPayrollAddonRequiredError(err: unknown): boolean {
  if (!isForbiddenTrpcError(err)) return false;
  const message = err instanceof Error ? err.message : undefined;
  return message === PAYROLL_ADDON_REQUIRED_MESSAGE;
}

/**
 * Whether `err` is ANY tRPC BAD_REQUEST rejection — same duck-typed shape
 * check as {@link isForbiddenTrpcError}/{@link isNotFoundTrpcError}. Used by
 * `data/use_employee_mutations.ts` to classify `payroll.createEmployee`/
 * `payroll.updateEmployee`'s cross-field validation failures
 * (`validateEmployeeProfile`/`assertValidNicAndEpf`,
 * `apps/api/src/trpc/routers/payroll.ts`) — those throw a deliberately
 * caller-safe, non-internal message (e.g. "A salaried employee requires a
 * positive monthly_salary_cents"), so it is safe to surface `err.message`
 * directly to the form, same as `apps/ledger-web`'s `toast_trpc_error`
 * convention of showing 4xx messages verbatim (docs/security-rules.md's
 * "never leak internal details" targets stack traces/SQL/file paths, not a
 * deliberately-authored validation string).
 */
export function isBadRequestTrpcError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('data' in err)) {
    return false;
  }
  const data = (err as { data?: unknown }).data;
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { code?: unknown }).code === 'BAD_REQUEST'
  );
}

/**
 * Whether `err` is a tRPC `CONFLICT` (HTTP 409). `payroll.scheduleCompensationChange`
 * / `cancelCompensationChange` raise it for an overlapping DRAFT pay run, a
 * same-date entry, and a mid-period currency change -- each also carries a
 * distinguishing `ledgerCode` (see {@link extractLedgerCode}) and a
 * caller-safe, human-readable `message` that is shown verbatim.
 */
export function isConflictTrpcError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('data' in err)) {
    return false;
  }
  const data = (err as { data?: unknown }).data;
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { code?: unknown }).code === 'CONFLICT'
  );
}

/**
 * Reads the stable, machine-readable `data.ledgerCode` the errorFormatter
 * attaches to any TRPCError thrown via `ledgerError()`
 * (`apps/api/src/lib/error_codes.ts`) — `null` for a plain `TRPCError` (no
 * `ledgerCode` set) or a non-tRPC-shaped value.
 */
export function extractLedgerCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null || !('data' in err)) {
    return null;
  }
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;
  const code = (data as { ledgerCode?: unknown }).ledgerCode;
  return typeof code === 'string' ? code : null;
}

/**
 * The exact `ledgerCode` `assertPayrollEmployeeWithinPlanCap`
 * (`apps/api/src/lib/plan_cap_gate.ts`) attaches to the FORBIDDEN it throws
 * when `payroll.createEmployee` would push an entity over its per-plan
 * free-employee allowance (Lite/Starter 3, Growth 5, Scale 10) — see
 * `apps/api/src/lib/error_codes.ts`'s `PLAN_PAYROLL_EMPLOYEE_LIMIT_EXCEEDED`.
 */
export const PLAN_PAYROLL_EMPLOYEE_LIMIT_EXCEEDED_CODE =
  'LEDGER_PLAN_PAYROLL_EMPLOYEE_LIMIT_EXCEEDED';

/**
 * Whether `err` is SPECIFICALLY the per-entity payroll free-employee
 * allowance cap rejection — as opposed to any other FORBIDDEN
 * `payroll.createEmployee` can throw (missing OAuth scope, missing `payroll`
 * add-on entitlement — see {@link isPayrollAddonRequiredError}). The
 * errorFormatter does not forward the cap's `limit`/`plan`/`currentCount`
 * cause fields onto the wire for this specific error (only `ledgerCode`) —
 * the backend's own `message` is the caller-safe, human-readable text that
 * already includes the limit and plan name, so callers should display
 * `err.message` directly rather than trying to reconstruct it from
 * unavailable cause fields.
 */
export function isPlanPayrollEmployeeCapExceededError(err: unknown): boolean {
  return extractLedgerCode(err) === PLAN_PAYROLL_EMPLOYEE_LIMIT_EXCEEDED_CODE;
}

/**
 * `payroll.linkSelfAsEmployee` found no employee row for the caller's email
 * and was given no `profile` -- `PAYROLL_SELF_LINK_PROFILE_REQUIRED` in
 * `apps/api/src/lib/error_codes.ts`. The client collects pay details and
 * retries. Pinned literal, mirroring
 * {@link PLAN_PAYROLL_EMPLOYEE_LIMIT_EXCEEDED_CODE}.
 */
export const PAYROLL_SELF_LINK_PROFILE_REQUIRED_CODE =
  'LEDGER_PAYROLL_SELF_LINK_PROFILE_REQUIRED';

/** Whether `err` is SPECIFICALLY the self-link "profile required" rejection. */
export function isSelfLinkProfileRequiredError(err: unknown): boolean {
  return extractLedgerCode(err) === PAYROLL_SELF_LINK_PROFILE_REQUIRED_CODE;
}

/**
 * `createEmployee`/`updateEmployee` refusing an `employee_number` another
 * employee of the organisation already holds -- `PAYROLL_EMPLOYEE_NUMBER_TAKEN`
 * in `apps/api/src/lib/error_codes.ts` (CONFLICT). Pinned literal, mirroring
 * {@link PLAN_PAYROLL_EMPLOYEE_LIMIT_EXCEEDED_CODE}.
 */
export const PAYROLL_EMPLOYEE_NUMBER_TAKEN_CODE =
  'LEDGER_PAYROLL_EMPLOYEE_NUMBER_TAKEN';

/** Whether `err` is SPECIFICALLY the duplicate-employee-number refusal. */
export function isEmployeeNumberTakenError(err: unknown): boolean {
  return extractLedgerCode(err) === PAYROLL_EMPLOYEE_NUMBER_TAKEN_CODE;
}

/**
 * The `{requiredTier, currentTier}` pair the errorFormatter (`apps/api/src/
 * trpc/init.ts`) attaches to `shape.data` for ANY FORBIDDEN raised by
 * `enforceFeature`/`assertFeatureEnabled` (`apps/api/src/lib/plan_gate.ts`) —
 * e.g. `expenseClaim.list`/`.approve`/`.reject`'s Starter+ `expense_claims`
 * plan gate (#22792). Unlike {@link isPayrollAddonRequiredError} (which has
 * to fall back to matching a pinned message literal because its gate
 * attaches no machine-readable cause), the plan-tier gate already puts
 * structured data on the wire — reading it directly is more robust than a
 * message match.
 */
export interface PlanGateInfo {
  requiredTier: string;
  currentTier: string;
}

/** Reads {@link PlanGateInfo} off a caught tRPC error, or `null` if this isn't a plan-tier-gated FORBIDDEN. */
export function extractPlanGateInfo(err: unknown): PlanGateInfo | null {
  if (typeof err !== 'object' || err === null || !('data' in err)) {
    return null;
  }
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;
  const requiredTier = (data as { requiredTier?: unknown }).requiredTier;
  const currentTier = (data as { currentTier?: unknown }).currentTier;
  if (typeof requiredTier !== 'string' || typeof currentTier !== 'string') {
    return null;
  }
  return { requiredTier, currentTier };
}

/** Whether `err` is a plan-tier-gated FORBIDDEN (a `requiredTier`/`currentTier` pair is present on the wire). */
export function isPlanGateError(err: unknown): boolean {
  return extractPlanGateInfo(err) !== null;
}

/**
 * Whether `err` is the SUBSCRIPTION gate FORBIDDEN `enforceFeature`
 * (`apps/api/src/lib/plan_gate.ts`) throws FIRST, before the plan-tier check
 * — an entity whose subscription itself is `none`/`canceled`, as opposed to
 * one that is active but on too low a plan tier ({@link isPlanGateError}).
 * The errorFormatter attaches `subscriptionRequired: true` (not
 * `requiredTier`/`currentTier`) for this case.
 */
export function isSubscriptionGateError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('data' in err)) {
    return false;
  }
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return false;
  return (
    (data as { subscriptionRequired?: unknown }).subscriptionRequired === true
  );
}

/**
 * The `ledgerCode` `ledgerError('BAD_REQUEST', ..., ErrorCode.FX_RATE_REQUIRED)`
 * attaches (`apps/api/src/lib/error_codes.ts`) when `expenseClaim.approve`
 * refuses to approve a foreign-currency claim rather than post it to the GL
 * at a silent 1.0 exchange rate (#21805, #22792). Pinned literal, mirroring
 * {@link PLAN_PAYROLL_EMPLOYEE_LIMIT_EXCEEDED_CODE}'s precedent.
 */
export const FX_RATE_REQUIRED_LEDGER_CODE = 'LEDGER_FX_RATE_REQUIRED';

/** Whether `err` is SPECIFICALLY the `FX_RATE_REQUIRED` rejection on `expenseClaim.approve` (#21805). */
export function isFxRateRequiredError(err: unknown): boolean {
  return extractLedgerCode(err) === FX_RATE_REQUIRED_LEDGER_CODE;
}

/**
 * The `ledgerCode`s the API attaches (`apps/api/src/lib/error_codes.ts`)
 * when an employee-only action targets an independent contractor
 * (`PAYROLL_CONTRACTOR_NOT_ELIGIBLE` -- leave, leave payout, gratuity,
 * pesangon, ESS invite) or a draft pay run still carries a worker who was
 * switched to contractor (`PAYROLL_PAY_RUN_HAS_CONTRACTOR`). Pinned
 * literals, mirroring {@link PLAN_PAYROLL_EMPLOYEE_LIMIT_EXCEEDED_CODE}.
 */
export const PAYROLL_CONTRACTOR_NOT_ELIGIBLE_CODE =
  'LEDGER_PAYROLL_CONTRACTOR_NOT_ELIGIBLE';
export const PAYROLL_PAY_RUN_HAS_CONTRACTOR_CODE =
  'LEDGER_PAYROLL_PAY_RUN_HAS_CONTRACTOR';

/**
 * `updateEmployee` refusing to switch a worker to contractor while they are
 * on a DRAFT pay run (that draft could then never be approved) --
 * `PAYROLL_CONTRACTOR_ON_DRAFT_PAY_RUN` in `apps/api/src/lib/error_codes.ts`.
 */
export const PAYROLL_CONTRACTOR_ON_DRAFT_PAY_RUN_CODE =
  'LEDGER_PAYROLL_CONTRACTOR_ON_DRAFT_PAY_RUN';
/**
 * `updateEmployee` refusing to switch an EMPLOYEE with history (approved/paid
 * pay-run lines, a gratuity provision, a leave balance or leave grants) to
 * contractor -- `PAYROLL_CONTRACTOR_HAS_EMPLOYEE_HISTORY` in
 * `apps/api/src/lib/error_codes.ts`. The user terminates + settles the
 * employee and records the contractor as a new worker.
 */
export const PAYROLL_CONTRACTOR_HAS_EMPLOYEE_HISTORY_CODE =
  'LEDGER_PAYROLL_CONTRACTOR_HAS_EMPLOYEE_HISTORY';
/**
 * `createEmployee`/`updateEmployee` refusing an FTE (`fte_bps`) for a worker
 * who is not part-time -- `PAYROLL_FTE_NOT_PART_TIME` in
 * `apps/api/src/lib/error_codes.ts`. (An out-of-range FTE is rejected by the
 * API's zod input schema as a plain validation error, not with this code.)
 */
export const PAYROLL_FTE_NOT_PART_TIME_CODE =
  'LEDGER_PAYROLL_FTE_NOT_PART_TIME';

/** Whether `err` is SPECIFICALLY `approvePayRun`'s refusal of a draft that now carries a contractor. */
export function isPayRunHasContractorError(err: unknown): boolean {
  return extractLedgerCode(err) === PAYROLL_PAY_RUN_HAS_CONTRACTOR_CODE;
}

const WORKER_TYPE_LEDGER_CODES: ReadonlySet<string> = new Set([
  PAYROLL_CONTRACTOR_NOT_ELIGIBLE_CODE,
  PAYROLL_PAY_RUN_HAS_CONTRACTOR_CODE,
  PAYROLL_CONTRACTOR_ON_DRAFT_PAY_RUN_CODE,
  PAYROLL_CONTRACTOR_HAS_EMPLOYEE_HISTORY_CODE,
  PAYROLL_FTE_NOT_PART_TIME_CODE,
]);

/**
 * The `ledgerCode` of a worker-type refusal, or `null` when `err` is not
 * one. Lets a never-throwing mutation wrapper classify the failure without
 * holding a `t` function; render it with {@link workerTypeCodeMessage}.
 */
export function workerTypeErrorCode(err: unknown): string | null {
  const code = extractLedgerCode(err);
  return code !== null && WORKER_TYPE_LEDGER_CODES.has(code) ? code : null;
}

/**
 * The translated message for a worker-type `ledgerCode`, or `null` for any
 * other code. Branches on the stable code, never on the (English,
 * changeable) message. Each key is a literal `t()` call so the i18n
 * extractor and orphan check see it. `t` is the caller's
 * `useTranslation('wp')` function.
 */
export function workerTypeCodeMessage(
  code: string | null,
  t: (key: string) => string,
): string | null {
  if (code === PAYROLL_CONTRACTOR_NOT_ELIGIBLE_CODE) {
    return t('errors.contractor_not_eligible');
  }
  if (code === PAYROLL_PAY_RUN_HAS_CONTRACTOR_CODE) {
    return t('errors.pay_run_has_contractor');
  }
  if (code === PAYROLL_CONTRACTOR_ON_DRAFT_PAY_RUN_CODE) {
    return t('errors.contractor_on_draft_pay_run');
  }
  if (code === PAYROLL_CONTRACTOR_HAS_EMPLOYEE_HISTORY_CODE) {
    return t('errors.contractor_has_employee_history');
  }
  if (code === PAYROLL_FTE_NOT_PART_TIME_CODE) {
    return t('errors.fte_not_part_time');
  }
  return null;
}

/**
 * The translated message for a worker-type refusal, or `null` when `err`
 * is not one. `t` is the caller's `useTranslation('wp')` function.
 */
export function workerTypeErrorMessage(
  err: unknown,
  t: (key: string) => string,
): string | null {
  return workerTypeCodeMessage(workerTypeErrorCode(err), t);
}
